import { Component, NgZone, Renderer2 } from '@angular/core';
import { GridComponent } from '@progress/kendo-angular-grid';
import { DestroyBase } from 'src/app/components/common/ng-destroy-base';
import { RaciService } from '../../services/raci.service';
import { take } from 'rxjs/operators';
import { AppStorageService } from '../../../../services/app-storage.service';

/**
 * Handles raci participants pane component
 */
@Component({
  selector: 'raci-participants-pane',
  templateUrl: './raci-participants-pane.component.html',
  styleUrls: ['./raci-participants-pane.component.css'],
})
export class RaciParticipantsPaneComponent extends DestroyBase {

  /**
   * Constructor
   * @param service RaciService
   * @param renderer Renderer2
   * @param zone NgZone
   */
  constructor(private service: RaciService,
    private appStorage: AppStorageService,
    private renderer: Renderer2,
    public zone: NgZone) {
    super();
  }

  /**
   * The project data
   */
  project: any;

  /**
   * The list of participants
   */
  public participantsList: any;

  /**
   * Array to store participants grouped by role
   */
  public participantsByRole: Array<{ "key": string, "value": Array<any>, "shouldCollapse": false }> = [];

  /**
   * Array to store filtered participants grouped by role
   */
  public filteredParticipantsByRole: Array<{ "key": string, "value": Array<any>, "shouldCollapse": false }> = [];

  /**
   * Search term used for filtering participants
   */
  searchTerm = "";

  /**
   * Drop index used for drag and drop functionality
   */
  dropIndex: number;

  /**
   * Reference to the current grid component
   */
  currentGridRef: GridComponent;

  /**
   * Flag indicating whether to show the "No participants" info message
   */
  showNoParticipantsInfoMessage = false;

  /**
   * The portfolio data
   */
  portfolio: any;

  /**
   * The project ID
   */
  projectId: number;

  /**
    * Lifecycle hook that is called after the component is initialized
   */
  ngOnInit(): void {
    this.portfolio = this.appStorage.getSelectedPortfolio();
    this.projectId = this.portfolio.ProjectID;

    this.subscriptions.add(this.service.getParticipants(false, null, this.projectId).subscribe(data => {
      this.processParticipants(data)
    }));

    this.filteredParticipantsByRole = this.participantsByRole;
    this.setCollapsed();
  }

  /**
   * Process participants data
   * @param data Participants data
   */
  private processParticipants(data: any) {
    this.participantsList = data;
    this.addDraggableRows();
    this.participantsList.forEach(participant => {
      const role = participant.projectRole ? participant.projectRole : "Other users";
      this.addParticipantToList(this.participantsByRole, role, participant);
    });
  }

  /**
   * Sets should be collapsed of participants section
   * */
  setCollapsed() {
    this.filteredParticipantsByRole.forEach(participant => {
      participant.shouldCollapse = false;
    })
  }

  /**
   * Search user
* */
  public searchUsers() {
    this.filteredParticipantsByRole = [];
    this.participantsByRole.forEach(role => {
      role.value.forEach(participant => {
        if (participant.name.toLowerCase().includes(this.searchTerm.toLowerCase())) {
          this.addParticipantToList(this.filteredParticipantsByRole, role, participant);
          this.addDraggableRows();
        }
      });
    });
    this.showNoParticipantsInfoMessage = this.filteredParticipantsByRole.length < 1;
  }

  /**
   * Add participant in certain list
   * @param list List
   * @param role Role
   * @param participant Participant
   */
  private addParticipantToList(list: any, role: any, participant: any) {
    if (!list.find(role => { return role.key == participant.projectRole })) {
      list.push({ "key": participant.projectRole, "value": [] });
      list.find(role => { return role.key == participant.projectRole }).value.push(participant);
    }
    else {
      list.find(role => { return role.key == participant.projectRole }).value.push(participant);
    }
  }

  /**
   * Adds drag listeners to grid rows
   * */
  public setDraggableRows(): void {
    const gridRows: Array<HTMLTableElement> = this.getAllRows();
    gridRows.forEach((item: HTMLElement) => {
      this.renderer.setAttribute(item, "draggable", "true");
      this.addDragListeners(item);
    });
  }

  /**
   *  Retrieves all grid rows
   * */
  public getAllRows(): Array<HTMLTableElement> {
    return Array.from(document.querySelectorAll(".raci-participants-grid tr"));
  }

  /**
   * Adds drag event listeners
   * @param item Item to which the drag event listener is being set
   */
  public addDragListeners(item: any): void {
    item.addEventListener("dragstart", (e: DragEvent) => {
      const rowIndex: number = item.rowIndex;
      const gridId = item.closest('.kendo-white-grid').id;

      if (rowIndex === null) {
        return;
      }

      const referenceParticipantsList = this.participantsByRole.find(role => role.key == gridId).value.filter(x => x.name == item.innerText);
      const selectedParticipant = referenceParticipantsList.length > 1 ? referenceParticipantsList[rowIndex] : referenceParticipantsList[0];
      const newParticipant = this.createNewApprovalParticipant(selectedParticipant);
      const dataItem = JSON.stringify(newParticipant);
      e.dataTransfer.setData("text/plain", dataItem);
    });
  }

  /**
   * Drag start action
   * @param e Event
   * @param grid Grid from which the item is being dragged
   */
  public onDragStart(e: DragEvent, grid: GridComponent): void {
    this.currentGridRef = grid;
    const draggedElement: string = (e.target as HTMLElement).innerText;
    if (draggedElement.includes("Name")) {
      e.preventDefault();
    }
  }

  /**
   * On drop action
   * @param e Event
   * @param dragStartGridRef Grid Component from which the item is being dragged
   * @param droppedRowGridRef Grid Component into which the item is being dropped
   */
  public onDrop(e: DragEvent): void {
    this.addDraggableRows();
  }

  /**
   * Adds draggable rows to the grid
   * */
  private addDraggableRows() {
    this.subscriptions.add(this.zone.onStable.pipe(take(1)).subscribe(() => {
      this.destroyListeners();
      this.setDraggableRows();
    }));
  }

  /**
   * Sets dorp index
   * @param e Event
   * @param grid Kedno grid
   */
  public onDragOver(e: DragEvent, grid: GridComponent): void {
    e.preventDefault();
    this.dropIndex = this.participantsList.length + 1;
  }

  /**
  * Creates new participant from user data
  * */
  private createNewApprovalParticipant(user: any): any {
    return {
      "oid": user.userId,
      "name": user.name,
      "projectRoleId": user.projectRoleId
    };
  }

  /**
  * Destroys drag start event listeners
  * */
  private destroyListeners(): void {
    const tableRows: Array<HTMLElement> = this.getAllRows();
    tableRows.forEach((item) => {
      item.removeEventListener("dragstart", () => {
        //Nothing
      });
    });
  }

  /**
   * Expand or collapse participants section
   * @param shouldCollapseSector Indicating if a specific sector should be collapsed
   * @param sectorKey Sector key that will be collapsed
   */
  expandCollapseSector(shouldCollapseSector, sectorKey) {
    if (this.filteredParticipantsByRole.find(x => x.key == sectorKey)) {
      this.filteredParticipantsByRole.find(x => x.key == sectorKey).shouldCollapse = shouldCollapseSector;
    }

    this.addDraggableRows();
  }
}
